import { create } from 'zustand';
import { http } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { toast } from 'sonner';
import type { RadAvailabilityResponse, RadAvailabilityByDate } from '../types/radiologist';

type RadAvailabilityState = {
  // Availability grouped by rad_id, then by date -> active half-hours
  availability: Record<number, RadAvailabilityByDate>;
  loadingByRad: Record<number, boolean>;
  errorByRad: Record<number, string | undefined>;
};

type AvailabilityEntry = { date: string; halfhours: number[] };
type AvailabilityDayPayload = RadAvailabilityResponse["days"][number];
const HALFHOURS_PER_DAY = 48;
const inFlightAvailabilityFetches = new Map<string, Promise<void>>();

function normalizeRadIds(radIds: number[]): number[] {
  return Array.from(new Set(radIds)).sort((a, b) => a - b);
}

function getAvailabilityFetchKey(
  radIds: number[],
  startDate: string,
  endDate: string,
  outpatient: boolean,
): string {
  return `${outpatient ? 'op' : 'main'}|${radIds.join(',')}|${startDate}|${endDate}`;
}

function toActiveHalfhours(day: AvailabilityDayPayload): number[] {
  const activeHalfhours: number[] = [];
  day.halfhours.forEach((isActive, halfhour) => {
    if (isActive) activeHalfhours.push(halfhour);
  });
  return activeHalfhours;
}

type RadAvailabilityActions = {
  // Fetch availability for a radiologist within a date range
  fetch: (radIds: number[], startDate: string, endDate: string, signal?: AbortSignal) => Promise<void>;
  
  // Bulk upsert availability (replaces half-hours for given dates)
  save: (radId: number, entries: AvailabilityEntry[]) => Promise<void>;
  
  // Clear availability for a date range
  clear: (radId: number, startDate: string, endDate: string) => Promise<void>;
  
  // Get active half-hours for a rad on a specific date
  getActiveHalfhours: (radId: number, date: string) => number[];
};

/**
 * Builds a radiologist-availability store bound to one calendar scope.
 * `outpatient=false` is the main schedule; `outpatient=true` is the Outpatient
 * schedule. Each scope reads/writes only its own rows on the backend, so the two
 * calendars never clobber each other. Two singleton instances are exported below.
 */
function createRadAvailabilityStore(outpatient: boolean) {
  return create<RadAvailabilityState & RadAvailabilityActions>((set, get) => ({
  availability: {},
  loadingByRad: {},
  errorByRad: {},

  fetch: async (radIds, startDate, endDate, signal) => {
    const normalizedRadIds = normalizeRadIds(radIds);
    if (normalizedRadIds.length === 0) return;

    const fetchKey = getAvailabilityFetchKey(normalizedRadIds, startDate, endDate, outpatient);
    const inFlightFetch = inFlightAvailabilityFetches.get(fetchKey);
    if (inFlightFetch) return inFlightFetch;

    const fetchPromise = (async () => {
      // Mark all requested rads as loading and clear their errors
      const currentLoading = get().loadingByRad;
      const currentErrors = get().errorByRad;
      const loadingByRad = { ...currentLoading };
      const errorByRad = { ...currentErrors };

      for (const id of normalizedRadIds) {
        loadingByRad[id] = true;
        errorByRad[id] = undefined;
      }

      set({ loadingByRad, errorByRad });
      
      try {
        const params = new URLSearchParams({ rad_ids: normalizedRadIds.join(','), start_date: startDate, end_date: endDate, outpatient: String(outpatient) });

        const data = await http<RadAvailabilityResponse[]>(
          `/api/v1/radiologists/ids/availability?${params.toString()}`,
          {},
          signal
        );

        const availabilityState = { ...get().availability };
        const updatedLoading = { ...get().loadingByRad };
        const updatedErrors = { ...get().errorByRad };

        for (const id of normalizedRadIds) {
          const radData = data.find(d => d.rad_id === id);

          if (!radData) {
            updatedLoading[id] = false;
            updatedErrors[id] = 'No availability data returned for this radiologist';
            continue;
          }

          // Convert backend format:
          // { days: [{date, halfhours: boolean[48]}] } -> { [date]: number[] }
          // Merge with existing data instead of replacing
          const existing = availabilityState[id] || {};
          const byDate: RadAvailabilityByDate = { ...existing };
          
          for (const day of radData.days) {
            const activeHalfhours = toActiveHalfhours(day);
            if (activeHalfhours.length > 0) {
              byDate[day.date] = activeHalfhours;
            } else {
              // If no active hours for this date, mark it as empty (date was fetched but has no availability)
              byDate[day.date] = [];
            }
          }

          availabilityState[id] = byDate;
          updatedLoading[id] = false;
        }

        set({
          availability: availabilityState,
          loadingByRad: updatedLoading,
          errorByRad: updatedErrors,
        });
      } catch (err) {
        const message = getErrorMessage(err);
        const updatedLoading = { ...get().loadingByRad };
        const updatedErrors = { ...get().errorByRad };

        for (const id of normalizedRadIds) {
          updatedLoading[id] = false;
          updatedErrors[id] = message;
        }

        set({
          loadingByRad: updatedLoading,
          errorByRad: updatedErrors,
        });
      }
    })();

    inFlightAvailabilityFetches.set(fetchKey, fetchPromise);
    try {
      await fetchPromise;
    } finally {
      inFlightAvailabilityFetches.delete(fetchKey);
    }
  },

  save: async (radId, entries) => {
    try {
      // Convert entries [{date, hours: number[]}] to half-hour granularity items.
      const items: { date: string; halfhour: number; active: boolean }[] = [];
      
      for (const entry of entries) {
        // For each date, send all 48 half-hours.
        for (let halfhour = 0; halfhour < HALFHOURS_PER_DAY; halfhour++) {
          items.push({
            date: entry.date,
            halfhour,
            active: entry.halfhours.includes(halfhour),
          });
        }
      }
      
      await http(`/api/v1/radiologists/${radId}/availability`, {
        method: 'PUT',
        body: JSON.stringify({ items, outpatient }),
      });
      
      // Update local state
      const current = get().availability[radId] || {};
      const updated = { ...current };
      
      for (const entry of entries) {
        if (entry.halfhours.length > 0) {
          updated[entry.date] = entry.halfhours.sort((a, b) => a - b);
        } else {
          delete updated[entry.date];
        }
      }
      
      set({
        availability: {
          ...get().availability,
          [radId]: updated
        }
      });
      
      toast.success('Availability saved');
    } catch (err) {
      const message = getErrorMessage(err);
      toast.error('Failed to save availability', { description: message });
      throw err;
    }
  },

  clear: async (radId, startDate, endDate) => {
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate, outpatient: String(outpatient) });
      await http(`/api/v1/radiologists/${radId}/availability?${params}`, {
        method: 'DELETE',
      });
      
      // Update local state - remove all dates in range
      const current = get().availability[radId] || {};
      const updated = { ...current };
      
      for (const date of Object.keys(updated)) {
        if (date >= startDate && date <= endDate) {
          delete updated[date];
        }
      }
      
      set({
        availability: {
          ...get().availability,
          [radId]: updated
        }
      });
    } catch (err) {
      const message = getErrorMessage(err);
      toast.error('Failed to clear availability', { description: message });
      throw err;
    }
  },

  getActiveHalfhours: (radId, date) => {
    return get().availability[radId]?.[date] || [];
  },
  }));
}

/** Main-schedule availability (outpatient=false). Unchanged behavior. */
export const useRadAvailabilityStore = createRadAvailabilityStore(false);

/** Outpatient-schedule availability (outpatient=true). Separate scope/cache. */
export const useOutpatientAvailabilityStore = createRadAvailabilityStore(true);
